/**
 * Phase 5.4a-csv-conflicts (2026-05-15) — Multi-step CSV/Excel import flow
 * for the inventory.
 *
 * Step machine:
 *   upload    → file picker, parse client-side, validate rows
 *   strategy  → backend /import-preview classifies new vs conflicts,
 *               user picks a global policy (update / duplicate / skip /
 *               per_line). If `per_line` → go to step `perline`.
 *   perline   → CSVConflictResolver renders the cell-by-cell choices.
 *   applying  → call /import-apply with decisions.
 *   done      → result counters (created / updated / skipped / merged /
 *               errors) and shortcut buttons.
 *
 * Encoding auto-detect UTF-8/Windows-1252, separator `,`/`;`, .csv/.xls/.xlsx,
 * file > 5 MB → reject, > 1000 lignes → confirmation.
 */
import React, { useMemo, useRef, useState } from 'react';
import {
  X, Upload, FileSpreadsheet, AlertTriangle, CheckCircle2, Download, Loader2,
  ArrowRight, ArrowLeft, ListChecks, Sparkles,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { useAuth } from '../contexts/AuthContext';
import CSVConflictResolver from './CSVConflictResolver';

// ── Column name mapping (lowercased keys) ────────────────────────────────
const COLUMN_ALIASES = {
  reference: ['reference', 'référence', 'ref', 'sku', 'code'],
  ean: ['ean13', 'ean', 'gencod', 'code_barre', 'codebarre', 'code barre', 'barcode'],
  designation: ['designation', 'désignation', 'libelle', 'libellé', 'nom', 'produit', 'name', 'description_courte'],
  prix_achat: ['prix_achat', 'prixachat', 'achat', 'p_achat', 'cout', 'coût', 'price_buy', 'cost'],
  prix_vente: ['prix_vente', 'prixvente', 'vente', 'p_vente', 'prix', 'price_sell', 'price'],
  emplacement: ['emplacement', 'lieu', 'rayon', 'tiroir'],
  emplacement_type: ['emplacement_type', 'type_emplacement', 'location', 'storage', 'stock_location'],
  quantite: ['quantite', 'quantité', 'qte', 'qty', 'qt', 'stock'],
  quantite_boutique: ['quantite_boutique', 'qte_boutique', 'qty_boutique', 'stock_boutique', 'boutique', 'shop_qty', 'shop'],
  quantite_depot: ['quantite_depot', 'qte_depot', 'qty_depot', 'stock_depot', 'depot', 'dépôt', 'warehouse', 'warehouse_qty'],
  seuil_alerte: ['seuil_alerte', 'seuil', 'alert', 'min_stock'],
  visible_boutique: ['visible_boutique', 'visible', 'shop_visible', 'web', 'online'],
  fournisseur: ['fournisseur', 'supplier', 'vendor'],
  categorie: ['categorie', 'catégorie', 'category', 'famille'],
  marque: ['marque', 'brand'],
  modele: ['modele', 'modèle', 'model'],
  couleur: ['couleur', 'color', 'colour'],
  description: ['description', 'commentaire', 'note'],
  // STEP 3 multi-magasin (2026-05-15)
  magasin_id: ['magasin_id', 'magasin', 'store', 'store_id', 'shop_id'],
};

const normaliseEmplacementType = (raw) => {
  const s = (raw || '').toString().trim().toLowerCase();
  if (!s) return null;
  if (/(depot|dépot|dépôt|warehouse|stock|entrepot|entrepôt)/.test(s)) return 'depot';
  if (/(boutique|shop|magasin|store|vente)/.test(s)) return 'boutique';
  return null;
};

const FIELD_LABELS = {
  reference: 'Référence', ean: 'EAN13', designation: 'Désignation',
  prix_achat: 'Prix achat', prix_vente: 'Prix vente',
  emplacement: 'Emplacement (libre)', emplacement_type: 'Boutique/Dépôt',
  quantite: 'Quantité', quantite_boutique: 'Qté boutique', quantite_depot: 'Qté dépôt',
  fournisseur: 'Fournisseur',
  categorie: 'Catégorie', marque: 'Marque', modele: 'Modèle',
  couleur: 'Couleur', description: 'Description',
  seuil_alerte: 'Seuil alerte', visible_boutique: 'Visible boutique',
};

const MAX_FILE_BYTES = 5 * 1024 * 1024;
const LARGE_ROW_THRESHOLD = 1000;

const normaliseHeader = (h) => (h || '').toString().trim().toLowerCase().replace(/[\s_-]+/g, '_');

const buildHeaderMap = (headers) => {
  const map = {};
  headers.forEach((rawHeader, idx) => {
    const norm = normaliseHeader(rawHeader);
    for (const [field, aliases] of Object.entries(COLUMN_ALIASES)) {
      if (aliases.some((a) => normaliseHeader(a) === norm)) {
        map[field] = idx;
        return;
      }
    }
  });
  return map;
};

const parseDecimal = (raw) => {
  if (raw === null || raw === undefined || raw === '') return null;
  if (typeof raw === 'number') return raw;
  const s = String(raw).trim().replace(/\s/g, '').replace(',', '.');
  if (!/^-?\d+(\.\d+)?$/.test(s)) return NaN;
  return parseFloat(s);
};

const parseInt2 = (raw) => {
  if (raw === null || raw === undefined || raw === '') return null;
  if (typeof raw === 'number') return Math.trunc(raw);
  const s = String(raw).trim().replace(/\s/g, '');
  if (!/^-?\d+$/.test(s)) return NaN;
  return parseInt(s, 10);
};

const detectAndDecodeCsv = (buffer) => {
  const utf8 = new TextDecoder('utf-8', { fatal: false }).decode(buffer);
  if (!utf8.includes('\uFFFD')) return utf8;
  return new TextDecoder('windows-1252').decode(buffer);
};

const detectSeparator = (sample) => {
  const semi = (sample.match(/;/g) || []).length;
  const comma = (sample.match(/,/g) || []).length;
  return semi > comma ? ';' : ',';
};

const parseCsv = (text, sep) => {
  const rows = [];
  let cur = '';
  let row = [];
  let inQuotes = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { cur += '"'; i += 1; } else { inQuotes = false; }
      } else { cur += ch; }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === sep) {
      row.push(cur); cur = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i += 1;
      row.push(cur); cur = '';
      if (row.length > 1 || row[0]?.trim()) rows.push(row);
      row = [];
    } else { cur += ch; }
  }
  if (cur || row.length) { row.push(cur); rows.push(row); }
  return rows.filter((r) => r.some((c) => (c || '').toString().trim()));
};

const parseFile = async (file) => {
  const buffer = await file.arrayBuffer();
  const lower = (file.name || '').toLowerCase();
  if (lower.endsWith('.xls') || lower.endsWith('.xlsx')) {
    const wb = XLSX.read(buffer, { type: 'array' });
    const sheet = wb.Sheets[wb.SheetNames[0]];
    return XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: true });
  }
  const text = detectAndDecodeCsv(buffer);
  const sep = detectSeparator(text.slice(0, 4096));
  return parseCsv(text, sep);
};

const validateRow = (mapped, idx) => {
  const errors = [];
  if (!mapped.designation || !mapped.designation.toString().trim()) {
    errors.push('Désignation manquante');
  }
  const pa = parseDecimal(mapped.prix_achat);
  if (pa === null || Number.isNaN(pa) || pa < 0) errors.push('Prix achat invalide');
  const pv = parseDecimal(mapped.prix_vente);
  if (pv === null || Number.isNaN(pv) || pv < 0) errors.push('Prix vente invalide');

  const qBoutique = parseInt2(mapped.quantite_boutique);
  const qDepot = parseInt2(mapped.quantite_depot);
  const qSingle = parseInt2(mapped.quantite);
  const hasSplit = qBoutique !== null || qDepot !== null;
  let qty_boutique = 0;
  let qty_depot = 0;
  if (hasSplit) {
    if (qBoutique !== null) {
      if (Number.isNaN(qBoutique) || qBoutique < 0) errors.push('Quantité boutique invalide');
      else qty_boutique = qBoutique;
    }
    if (qDepot !== null) {
      if (Number.isNaN(qDepot) || qDepot < 0) errors.push('Quantité dépôt invalide');
      else qty_depot = qDepot;
    }
  } else if (qSingle === null || Number.isNaN(qSingle) || qSingle < 0) {
    errors.push('Quantité invalide');
  } else {
    const empType = normaliseEmplacementType(mapped.emplacement_type) || 'boutique';
    if (empType === 'depot') qty_depot = qSingle; else qty_boutique = qSingle;
  }
  if (errors.length === 0 && qty_boutique === 0 && qty_depot === 0) {
    errors.push('Quantité totale = 0 (rien à importer)');
  }

  const ean = (mapped.ean || '').toString().replace(/\s/g, '');
  if (ean && !/^\d{8,14}$/.test(ean)) errors.push(`EAN "${ean}" invalide (8-14 chiffres)`);

  return {
    row_index: idx,
    errors,
    normalised: {
      reference: (mapped.reference || '').toString().trim(),
      ean,
      designation: (mapped.designation || '').toString().trim(),
      prix_achat: pa || 0,
      prix_vente: pv || 0,
      qty_boutique,
      qty_depot,
      quantite_boutique: qty_boutique,
      quantite_depot: qty_depot,
      emplacement: (mapped.emplacement || '').toString().trim(),
      fournisseur: (mapped.fournisseur || '').toString().trim(),
      categorie: (mapped.categorie || '').toString().trim(),
      marque: (mapped.marque || '').toString().trim(),
      modele: (mapped.modele || '').toString().trim(),
      couleur: (mapped.couleur || '').toString().trim(),
      description: (mapped.description || '').toString().trim(),
      seuil_alerte: parseInt2(mapped.seuil_alerte) || undefined,
    },
  };
};

const downloadTemplate = () => {
  const cols = [
    'reference', 'ean13', 'designation', 'categorie', 'fournisseur',
    'emplacement', 'emplacement_type',
    'prix_achat', 'prix_vente',
    'quantite_boutique', 'quantite_depot', 'seuil_alerte', 'visible_boutique',
  ];
  const sample1 = ['IPH12-128-NR', '3700000000001', 'iPhone 12 128Go Noir', 'Smartphones', 'Apple',
    'Tiroir A3', 'boutique', '420.00', '599.00', '1', '0', '2', '1'];
  const sample2 = ['ECRAN-IPH13', '3700000000002', 'Ecran iPhone 13 OLED', 'Pieces', 'Mobile Sentrix',
    'Etagere D', 'depot', '45.00', '120.00', '0', '5', '2', '0'];
  const csv = [cols, sample1, sample2].map((row) => row.join(';')).join('\n') + '\n';
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = 'modele_import_inventaire.csv';
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
};

// ── Component ────────────────────────────────────────────────────────────
const STRATEGIES = [
  { id: 'update', label: '🔄 Mettre à jour', hint: 'Recommandé — refresh prix + additionne les quantités' },
  { id: 'duplicate', label: '➕ Créer en doublon (EAN auto)', hint: 'Génère un nouvel EAN13 unique pour chaque conflit' },
  { id: 'skip', label: '⏭️ Ignorer', hint: 'Ne touche pas aux articles existants' },
  { id: 'per_line', label: '⚙️ Décider ligne par ligne', hint: 'Affiche un tableau de résolution cellule par cellule' },
];

// ── Aperçu des changements — simule l'effet du `runApply` côté client ────
//   Renvoie un résumé : combien d'articles créés / mis à jour / ignorés,
//   et la liste détaillée des changements significatifs (prix, sortie de
//   rupture, changement d'emplacement, etc.).
const simulateChanges = (previewData, decisions, globalPolicy) => {
  if (!previewData) return null;
  const newCount = previewData.new?.length || 0;
  let toUpdate = 0;
  let toSkip = 0;
  let toDuplicate = 0;
  let toMerge = 0;
  const priceChanges = [];      // {designation, axis, old, new}
  const ruptureOut = [];        // qty 0 → >0
  const ruptureIn = [];         // qty >0 → 0
  const qtyChanges = [];        // {designation, axis, old, new}
  const labelChanges = [];      // {designation, field, old, new}
  const errors = [];

  (previewData.conflicts || []).forEach((c) => {
    const dec = decisions[c.row_index] || {};
    const action = (dec.action || globalPolicy);
    if (action === 'skip') { toSkip += 1; return; }
    if (action === 'duplicate') { toDuplicate += 1; return; }
    if (action === 'merge') { toMerge += 1; }
    if (action === 'update' || action === 'merge') {
      toUpdate += action === 'update' ? 1 : 0;
      const ex = c.existing || {};
      const csv = c.row || {};
      const dsg = csv.designation || ex.designation || 'Article';
      const fc = dec.field_choices || {};
      // Prices
      ['prix_achat', 'prix_vente'].forEach((f) => {
        const choice = fc[f] ?? 'csv';
        let val = ex[f];
        if (typeof choice === 'string' && choice !== 'existing' && choice !== 'csv') {
          val = parseFloat(choice);
        } else if (choice === 'csv') {
          const v = csv[f];
          if (v !== undefined && v !== null && v !== '') val = parseFloat(v);
        } else if (typeof choice === 'number') {
          val = choice;
        }
        if (val !== undefined && val !== null && !Number.isNaN(val) && Number(val) !== Number(ex[f] || 0)) {
          priceChanges.push({ designation: dsg, axis: f, old: Number(ex[f] || 0), new: Number(val) });
        }
      });
      // Quantities — apply add/replace/keep/<int>
      const qbAction = dec.qty_boutique_action ?? 'add';
      const qdAction = dec.qty_depot_action ?? 'add';
      const resolveQty = (act, oldVal, csvVal) => {
        if (typeof act === 'number') return act;
        if (act === 'replace') return csvVal;
        if (act === 'keep') return oldVal;
        return oldVal + csvVal;
      };
      const qbOld = Number(ex.quantite || 0);
      const qdOld = Number(ex.quantite_depot || 0);
      const qbCsv = Number(c.qty_imported_boutique || 0);
      const qdCsv = Number(c.qty_imported_depot || 0);
      const qbNew = resolveQty(qbAction, qbOld, qbCsv);
      const qdNew = resolveQty(qdAction, qdOld, qdCsv);
      if (qbNew !== qbOld) {
        qtyChanges.push({ designation: dsg, axis: 'boutique', old: qbOld, new: qbNew });
        if (qbOld === 0 && qbNew > 0) ruptureOut.push({ designation: dsg, axis: 'boutique', old: qbOld, new: qbNew });
        if (qbOld > 0 && qbNew === 0) ruptureIn.push({ designation: dsg, axis: 'boutique', old: qbOld, new: qbNew });
      }
      if (qdNew !== qdOld) {
        qtyChanges.push({ designation: dsg, axis: 'depot', old: qdOld, new: qdNew });
        if (qdOld === 0 && qdNew > 0) ruptureOut.push({ designation: dsg, axis: 'depot', old: qdOld, new: qdNew });
        if (qdOld > 0 && qdNew === 0) ruptureIn.push({ designation: dsg, axis: 'depot', old: qdOld, new: qdNew });
      }
      // Designation / categorie / fournisseur / emplacement changes
      ['designation', 'categorie', 'fournisseur', 'emplacement'].forEach((f) => {
        const choice = fc[f] ?? 'csv';
        let val = ex[f];
        if (typeof choice === 'string' && choice !== 'existing' && choice !== 'csv') val = choice;
        else if (choice === 'csv') {
          if (csv[f] !== undefined && csv[f] !== null && csv[f] !== '') val = csv[f];
        }
        if ((val || '') !== (ex[f] || '')) labelChanges.push({ designation: dsg, field: f, old: ex[f] || '∅', new: val || '∅' });
      });
    }
  });

  return {
    newCount, toUpdate, toSkip, toDuplicate, toMerge,
    priceChanges, qtyChanges, ruptureIn, ruptureOut, labelChanges, errors,
  };
};

const CSVImportModal = ({ open, onClose, onConfirm }) => {
  const { api } = useAuth();
  const [step, setStep] = useState('upload'); // upload | strategy | perline | preview-changes | applying | done
  const [busy, setBusy] = useState(false);
  const [parseError, setParseError] = useState('');
  const [rows, setRows] = useState([]);
  const [duplicateInFile, setDuplicateInFile] = useState([]);
  const [confirmedLarge, setConfirmedLarge] = useState(false);

  // Preview / strategy / apply state
  const [previewData, setPreviewData] = useState(null);
  const [globalPolicy, setGlobalPolicy] = useState('update');
  const [lineDecisions, setLineDecisions] = useState({});
  const [applyResult, setApplyResult] = useState(null);

  // STEP 3 multi-magasin — default magasin applied to rows that don't have
  // a `magasin_id` column. Pre-filled from the POS localStorage so a
  // device dedicated to M2 imports straight into M2.
  const [defaultMagasinId, setDefaultMagasinId] = useState(() => {
    try { return window.localStorage.getItem('pos_current_magasin_id') || 'M1'; } catch (_) { return 'M1'; }
  });
  const [magasinsList, setMagasinsList] = useState([]);
  React.useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await api.get('/magasins?active_only=true');
        setMagasinsList(r.data?.magasins || []);
      } catch (_) { /* fallback to M1/M2 */ }
    })();
  }, [open, api]);

  const inputRef = useRef(null);

  const valid = useMemo(() => rows.filter((r) => r.errors.length === 0), [rows]);
  const invalid = useMemo(() => rows.filter((r) => r.errors.length > 0), [rows]);

  const resetAll = () => {
    setStep('upload');
    setBusy(false);
    setParseError('');
    setRows([]);
    setDuplicateInFile([]);
    setConfirmedLarge(false);
    setPreviewData(null);
    setGlobalPolicy('update');
    setLineDecisions({});
    setApplyResult(null);
  };

  const handleClose = () => {
    resetAll();
    onClose?.();
  };

  // ── 1. Upload ──────────────────────────────────────────────────────────
  const handleFile = async (file) => {
    setParseError('');
    setRows([]);
    setDuplicateInFile([]);
    setConfirmedLarge(false);
    if (!file) return;
    if (file.size > MAX_FILE_BYTES) {
      setParseError(`Fichier trop volumineux (${(file.size / 1024 / 1024).toFixed(1)} Mo > 5 Mo).`);
      return;
    }
    setBusy(true);
    try {
      const matrix = await parseFile(file);
      if (matrix.length < 2) {
        setParseError('Aucune donnée détectée (1ʳᵉ ligne = en-têtes).');
        return;
      }
      const headerMap = buildHeaderMap(matrix[0]);
      const hasAnyQty = ['quantite', 'quantite_boutique', 'quantite_depot'].some((k) => k in headerMap);
      const required = ['designation', 'prix_achat', 'prix_vente'];
      const missing = required.filter((r) => !(r in headerMap));
      if (!hasAnyQty) missing.push('quantite');
      if (missing.length) {
        setParseError(`Colonnes obligatoires manquantes : ${missing.map((r) => FIELD_LABELS[r] || r).join(', ')}.`);
        return;
      }
      const parsed = matrix.slice(1).map((line, idx) => {
        const m = {};
        for (const [field, colIdx] of Object.entries(headerMap)) m[field] = line[colIdx];
        return validateRow(m, idx + 2);
      });
      const seen = new Map();
      const dups = [];
      parsed.forEach((p) => {
        const ean = p.normalised.ean;
        if (!ean) return;
        if (seen.has(ean)) dups.push({ ean, lines: [seen.get(ean), p.row_index] });
        else seen.set(ean, p.row_index);
      });
      setDuplicateInFile(dups);
      setRows(parsed);
    } catch (err) {
      setParseError(err.message || 'Erreur de parsing.');
    } finally {
      setBusy(false);
    }
  };

  // ── 2. Analyse conflits (call /import-preview) ─────────────────────────
  const runPreview = async () => {
    if (valid.length === 0) return;
    if (valid.length > LARGE_ROW_THRESHOLD && !confirmedLarge) {
      setConfirmedLarge(true);
      return;
    }
    setBusy(true);
    try {
      const payload = {
        rows: valid.map((v) => ({ row_index: v.row_index, ...v.normalised })),
        // STEP 3 multi-magasin — fallback applied to rows missing magasin_id
        default_magasin_id: defaultMagasinId,
      };
      const res = await api.post('/stock/import-preview', payload);
      setPreviewData(res.data);
      // If 0 conflicts → still go to strategy (info), user just clicks Continuer.
      setStep('strategy');
    } catch (e) {
      setParseError(e?.response?.data?.detail || 'Erreur d\'analyse côté serveur.');
    } finally {
      setBusy(false);
    }
  };

  // ── 3. Apply ───────────────────────────────────────────────────────────
  // Route the user through the "preview-changes" step first so they can
  // review what will actually happen before mutating the DB.
  const goToPreviewChanges = () => setStep('preview-changes');

  const runApply = async () => {
    if (!previewData) return;
    setBusy(true);
    setStep('applying');
    try {
      const idem = (window.crypto?.randomUUID && window.crypto.randomUUID()) || `imp-${Date.now()}`;
      const body = {
        default_policy: globalPolicy,
        // STEP 3 multi-magasin — magasin fallback for new rows missing the column
        default_magasin_id: defaultMagasinId,
        new: previewData.new,
        conflicts: previewData.conflicts,
        decisions: globalPolicy === 'per_line' ? lineDecisions : {},
      };
      const res = await api.post('/stock/import-apply', body, {
        headers: { 'Idempotency-Key': idem },
      });
      setApplyResult(res.data);
      setStep('done');
      onConfirm?.(res.data, { default_policy: globalPolicy });
    } catch (e) {
      setApplyResult({ errors: [e?.response?.data?.detail || 'Erreur lors de l\'import'] });
      setStep('done');
    } finally {
      setBusy(false);
    }
  };

  // ── Bulk shortcuts in perline view ─────────────────────────────────────
  const bulkAll = (partial) => {
    const next = {};
    (previewData?.conflicts || []).forEach((c) => { next[c.row_index] = { ...(lineDecisions[c.row_index] || {}), ...partial }; });
    setLineDecisions(next);
  };
  const bulkSimilar = (conflict_type, partial) => {
    const next = { ...lineDecisions };
    (previewData?.conflicts || []).forEach((c) => {
      if (c.conflict_type === conflict_type) next[c.row_index] = { ...(next[c.row_index] || {}), ...partial };
    });
    setLineDecisions(next);
  };

  if (!open) return null;

  const stats = previewData?.stats || {};
  const stepIndex = ['upload', 'strategy', 'perline', 'preview-changes', 'applying', 'done'].indexOf(step);
  const changes = step === 'preview-changes' ? simulateChanges(previewData, lineDecisions, globalPolicy) : null;

  return (
    <div className="fixed inset-0 z-[120] bg-black/70 backdrop-blur flex items-center justify-center p-4" data-testid="csv-import-modal">
      <div className="w-full max-w-5xl bg-gray-950 border border-white/10 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]">
        <header className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <FileSpreadsheet className="w-6 h-6 text-emerald-400" />
            <div>
              <h2 className="text-base font-semibold">Importer un fichier CSV / Excel</h2>
              <p className="text-xs text-gray-500">
                Étape {Math.min(stepIndex + 1, 5)} / 5 — {{
                  upload: 'Choix du fichier', strategy: 'Conflits détectés',
                  perline: 'Résolution ligne par ligne',
                  'preview-changes': 'Aperçu des changements',
                  applying: 'Application…', done: 'Terminé',
                }[step]}
              </p>
            </div>
          </div>
          <button onClick={handleClose} className="p-2 rounded hover:bg-white/5" data-testid="csv-modal-close">
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="p-4 space-y-4 overflow-y-auto">

          {/* ───────── STEP 1 — UPLOAD ───────── */}
          {step === 'upload' && (
            <>
              {rows.length === 0 && (
                <>
                  <button
                    onClick={() => inputRef.current?.click()}
                    className="w-full border-2 border-dashed border-white/15 hover:border-emerald-400/50 rounded-2xl p-8 flex flex-col items-center gap-2 transition-colors text-gray-400 hover:text-gray-200"
                    data-testid="csv-dropzone"
                  >
                    <Upload className="w-8 h-8" />
                    <span className="text-sm font-medium">Cliquez pour choisir un fichier</span>
                    <span className="text-xs">ou glissez-déposez votre CSV / Excel</span>
                  </button>
                  <input
                    ref={inputRef}
                    type="file"
                    accept=".csv,.xls,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    hidden
                    onChange={(e) => handleFile(e.target.files?.[0])}
                    data-testid="csv-file-input"
                  />
                  <div className="flex items-center justify-between text-xs">
                    <button onClick={downloadTemplate} className="text-emerald-300 hover:underline flex items-center gap-1" data-testid="csv-download-template">
                      <Download className="w-3.5 h-3.5" /> Télécharger le modèle CSV
                    </button>
                    <span className="text-gray-600">reference, ean13, designation, prix_achat, prix_vente, quantite_boutique|quantite_depot…</span>
                  </div>
                </>
              )}

              {parseError && (
                <div className="bg-red-500/15 border border-red-500/30 text-red-200 rounded-xl p-3 text-sm flex items-start gap-2" data-testid="csv-parse-error">
                  <AlertTriangle className="w-4 h-4 mt-0.5" />
                  <div>{parseError}</div>
                </div>
              )}

              {rows.length > 0 && (
                <>
                  <div className="flex items-center justify-between gap-3 text-sm">
                    <div className="flex items-center gap-4">
                      <span className="flex items-center gap-1.5 text-emerald-300" data-testid="csv-valid-count">
                        <CheckCircle2 className="w-4 h-4" /> {valid.length} valides
                      </span>
                      <span className="flex items-center gap-1.5 text-red-300" data-testid="csv-invalid-count">
                        <AlertTriangle className="w-4 h-4" /> {invalid.length} en erreur
                      </span>
                      {duplicateInFile.length > 0 && (
                        <span className="text-amber-300 text-xs">⚠️ {duplicateInFile.length} EAN doublon(s) dans le fichier</span>
                      )}
                    </div>
                    <button onClick={() => { setRows([]); setParseError(''); }} className="text-xs text-gray-500 hover:text-white" data-testid="csv-reset">
                      Choisir un autre fichier
                    </button>
                  </div>

                  <div className="overflow-x-auto border border-white/10 rounded-xl">
                    <table className="w-full text-xs">
                      <thead className="bg-white/5 text-gray-400 sticky top-0">
                        <tr>
                          <th className="p-2 text-left">#</th>
                          <th className="p-2 text-left">Référence</th>
                          <th className="p-2 text-left">EAN</th>
                          <th className="p-2 text-left">Désignation</th>
                          <th className="p-2 text-right">P. Achat</th>
                          <th className="p-2 text-right">P. Vente</th>
                          <th className="p-2 text-right">Qté Bout.</th>
                          <th className="p-2 text-right">Qté Dépôt</th>
                          <th className="p-2 text-left">Erreurs</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.slice(0, 200).map((r) => (
                          <tr key={r.row_index} className={r.errors.length ? 'bg-red-500/10' : ''} data-testid={`csv-row-${r.row_index}`}>
                            <td className="p-2 text-gray-500">L{r.row_index}</td>
                            <td className="p-2 text-gray-200 truncate max-w-[100px]">{r.normalised.reference || <span className="text-gray-600">auto</span>}</td>
                            <td className="p-2 text-gray-300 font-mono">{r.normalised.ean || '—'}</td>
                            <td className="p-2 text-gray-100 truncate max-w-[200px]">{r.normalised.designation || <span className="text-red-400">vide</span>}</td>
                            <td className="p-2 text-right text-gray-300">{r.normalised.prix_achat.toFixed(2)}</td>
                            <td className="p-2 text-right text-gray-300">{r.normalised.prix_vente.toFixed(2)}</td>
                            <td className="p-2 text-right text-green-300">{r.normalised.qty_boutique || ''}</td>
                            <td className="p-2 text-right text-purple-300">{r.normalised.qty_depot || ''}</td>
                            <td className="p-2 text-red-300">{r.errors.join(' • ')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {rows.length > 200 && (
                      <p className="text-center text-xs text-gray-500 p-2">… aperçu limité aux 200 premières lignes ({rows.length} au total)</p>
                    )}
                  </div>
                </>
              )}
            </>
          )}

          {/* ───────── STEP 2 — STRATEGY ───────── */}
          {step === 'strategy' && previewData && (
            <>
              {/* STEP 3 multi-magasin — default magasin selector. Always shown
                  so the admin confirms the destination even when the CSV
                  includes a magasin_id column (it then only applies as
                  fallback for rows missing the column). */}
              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-3 flex items-center gap-3" data-testid="csv-default-magasin">
                <span className="text-cyan-200 text-sm font-semibold">Magasin par défaut :</span>
                <select
                  value={defaultMagasinId}
                  onChange={(e) => setDefaultMagasinId(e.target.value)}
                  className="input-dark py-1 px-2 text-sm font-semibold"
                  data-testid="csv-default-magasin-select"
                >
                  {(magasinsList.length ? magasinsList : [{code:'M1', name:'Magasin 1'}, {code:'M2', name:'Magasin 2'}]).map((m) => (
                    <option key={m.code} value={m.code}>{m.name} ({m.code})</option>
                  ))}
                </select>
                <span className="text-xs text-cyan-200/60">Appliqué aux lignes sans colonne <code>magasin_id</code>.</span>
              </div>

              {/* Stats cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                <div className="bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2.5">
                  <div className="text-2xl font-bold text-white">{stats.total}</div>
                  <div className="text-[11px] text-gray-500">lignes analysées</div>
                </div>
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl px-3 py-2.5">
                  <div className="text-2xl font-bold text-emerald-300">{stats.new || 0}</div>
                  <div className="text-[11px] text-emerald-200/60">nouveaux articles</div>
                </div>
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl px-3 py-2.5">
                  <div className="text-2xl font-bold text-amber-300">{stats.conflicts || 0}</div>
                  <div className="text-[11px] text-amber-200/60">conflits détectés</div>
                </div>
                <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl px-3 py-2.5">
                  <div className="text-2xl font-bold text-purple-300">{stats.designation_only || 0}</div>
                  <div className="text-[11px] text-purple-200/60">match désignation seule</div>
                </div>
              </div>

              {(previewData.warnings || []).length > 0 && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 text-amber-200 text-xs">
                  <strong className="block mb-1">Avertissements ({previewData.warnings.length}) :</strong>
                  <ul className="list-disc pl-4 max-h-24 overflow-y-auto">
                    {previewData.warnings.map((w, i) => <li key={i}>L{w.row_index} — {w.message}</li>)}
                  </ul>
                </div>
              )}

              {stats.conflicts === 0 ? (
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 text-emerald-200 text-sm">
                  ✅ Aucun conflit — les {stats.new} ligne(s) seront créées directement.
                </div>
              ) : (
                <>
                  <div className="text-sm text-gray-300 flex items-start gap-2 bg-white/5 border border-white/10 rounded-xl p-3">
                    <Sparkles className="w-4 h-4 text-emerald-400 mt-0.5" />
                    <div>
                      <strong>{stats.conflicts} conflit(s) sur {stats.total} lignes.</strong> Que faire pour les articles déjà présents en base ?
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {STRATEGIES.map((opt) => (
                      <button
                        key={opt.id}
                        onClick={() => setGlobalPolicy(opt.id)}
                        className={`text-left px-3 py-2.5 rounded-xl border transition-colors ${
                          globalPolicy === opt.id
                            ? 'bg-emerald-500/15 border-emerald-500/50 text-emerald-100'
                            : 'bg-white/[0.04] border-white/10 text-gray-300 hover:bg-white/[0.08]'
                        }`}
                        data-testid={`strategy-${opt.id}`}
                      >
                        <div className="text-sm font-semibold flex items-center gap-2">
                          {opt.label}
                          {globalPolicy === opt.id && <CheckCircle2 className="w-4 h-4 ml-auto text-emerald-400" />}
                        </div>
                        <div className="text-[11px] text-gray-500 mt-1">{opt.hint}</div>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </>
          )}

          {/* ───────── STEP 3 — PER LINE ───────── */}
          {step === 'perline' && previewData && (
            <CSVConflictResolver
              conflicts={previewData.conflicts}
              decisions={lineDecisions}
              onChange={setLineDecisions}
              onBulkAll={bulkAll}
              onBulkSimilar={bulkSimilar}
              maxRows={LARGE_ROW_THRESHOLD}
            />
          )}

          {/* ───────── STEP 4 — PREVIEW CHANGES ───────── */}
          {step === 'preview-changes' && changes && (
            <div className="space-y-3" data-testid="preview-changes">
              {/* Summary cards */}
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
                {[
                  { label: 'créé(s)', value: changes.newCount, cls: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300', tid: 'pc-created' },
                  { label: 'mis à jour', value: changes.toUpdate, cls: 'bg-blue-500/10 border-blue-500/30 text-blue-300', tid: 'pc-updated' },
                  { label: 'ignoré(s)', value: changes.toSkip, cls: 'bg-amber-500/10 border-amber-500/30 text-amber-300', tid: 'pc-skipped' },
                  { label: 'dupliqué(s)', value: changes.toDuplicate, cls: 'bg-fuchsia-500/10 border-fuchsia-500/30 text-fuchsia-300', tid: 'pc-duplicated' },
                  { label: 'fusionné(s)', value: changes.toMerge, cls: 'bg-purple-500/10 border-purple-500/30 text-purple-300', tid: 'pc-merged' },
                ].map((c) => (
                  <div key={c.label} className={`border rounded-lg px-2 py-2 ${c.cls}`} data-testid={c.tid}>
                    <div className="text-xl font-bold">{c.value}</div>
                    <div className="text-[10px] opacity-70">{c.label}</div>
                  </div>
                ))}
              </div>

              {/* Highlights */}
              {(changes.priceChanges.length > 0 || changes.qtyChanges.length > 0 || changes.ruptureOut.length > 0 || changes.ruptureIn.length > 0 || changes.labelChanges.length > 0) ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {changes.priceChanges.length > 0 && (
                    <div className="bg-white/[0.04] border border-white/10 rounded-xl p-3" data-testid="pc-prices">
                      <div className="text-xs font-semibold text-blue-300 mb-1">
                        💰 {changes.priceChanges.length} changement(s) de prix
                      </div>
                      <ul className="text-[11px] text-gray-300 space-y-0.5 max-h-32 overflow-y-auto pr-1">
                        {changes.priceChanges.slice(0, 50).map((p, i) => (
                          <li key={i}>
                            <span className="text-gray-400 truncate inline-block max-w-[150px] align-middle">{p.designation}</span>
                            {' '}<em className="text-gray-500">({p.axis === 'prix_achat' ? 'achat' : 'vente'})</em>
                            {' '}<span className="text-gray-500 line-through">{p.old.toFixed(2)}€</span>
                            {' '}<span className="text-blue-300 font-semibold">{p.new.toFixed(2)}€</span>
                            <span className={`ml-1 text-[10px] ${p.new > p.old ? 'text-amber-300' : 'text-emerald-300'}`}>
                              ({p.new > p.old ? '+' : ''}{((p.new - p.old)).toFixed(2)}€)
                            </span>
                          </li>
                        ))}
                        {changes.priceChanges.length > 50 && <li className="text-gray-500 italic">… et {changes.priceChanges.length - 50} de plus</li>}
                      </ul>
                    </div>
                  )}
                  {changes.ruptureOut.length > 0 && (
                    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3" data-testid="pc-rupture-out">
                      <div className="text-xs font-semibold text-emerald-300 mb-1">
                        ✨ {changes.ruptureOut.length} article(s) vont sortir de rupture
                      </div>
                      <ul className="text-[11px] text-gray-300 space-y-0.5 max-h-32 overflow-y-auto pr-1">
                        {changes.ruptureOut.slice(0, 30).map((r, i) => (
                          <li key={i}>
                            <span className="truncate inline-block max-w-[200px] align-middle">{r.designation}</span>
                            {' '}<em className="text-gray-500">({r.axis})</em>: 0 → <strong className="text-emerald-300">{r.new}</strong>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {changes.ruptureIn.length > 0 && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3" data-testid="pc-rupture-in">
                      <div className="text-xs font-semibold text-red-300 mb-1">
                        ⚠️ {changes.ruptureIn.length} article(s) vont passer en rupture
                      </div>
                      <ul className="text-[11px] text-gray-300 space-y-0.5 max-h-32 overflow-y-auto pr-1">
                        {changes.ruptureIn.slice(0, 30).map((r, i) => (
                          <li key={i}>
                            <span className="truncate inline-block max-w-[200px] align-middle">{r.designation}</span>
                            {' '}<em className="text-gray-500">({r.axis})</em>: <strong className="text-red-300">{r.old}</strong> → 0
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {changes.qtyChanges.length > 0 && (
                    <div className="bg-white/[0.04] border border-white/10 rounded-xl p-3" data-testid="pc-qty">
                      <div className="text-xs font-semibold text-cyan-300 mb-1">
                        📦 {changes.qtyChanges.length} variation(s) de quantité
                      </div>
                      <ul className="text-[11px] text-gray-300 space-y-0.5 max-h-32 overflow-y-auto pr-1">
                        {changes.qtyChanges.slice(0, 50).map((q, i) => (
                          <li key={i}>
                            <span className="truncate inline-block max-w-[180px] align-middle">{q.designation}</span>
                            {' '}<em className="text-gray-500">({q.axis})</em>: {q.old} → <strong className="text-cyan-300">{q.new}</strong>
                            <span className={`ml-1 text-[10px] ${q.new > q.old ? 'text-emerald-300' : 'text-amber-300'}`}>
                              ({q.new > q.old ? '+' : ''}{q.new - q.old})
                            </span>
                          </li>
                        ))}
                        {changes.qtyChanges.length > 50 && <li className="text-gray-500 italic">… et {changes.qtyChanges.length - 50} de plus</li>}
                      </ul>
                    </div>
                  )}
                  {changes.labelChanges.length > 0 && (
                    <div className="bg-white/[0.04] border border-white/10 rounded-xl p-3 md:col-span-2" data-testid="pc-labels">
                      <div className="text-xs font-semibold text-purple-300 mb-1">
                        ✏️ {changes.labelChanges.length} changement(s) de métadonnées
                      </div>
                      <ul className="text-[11px] text-gray-300 space-y-0.5 max-h-24 overflow-y-auto pr-1">
                        {changes.labelChanges.slice(0, 40).map((l, i) => (
                          <li key={i}>
                            <span className="truncate inline-block max-w-[180px] align-middle">{l.designation}</span>
                            {' '}<em className="text-gray-500">({l.field})</em>:{' '}
                            <span className="text-gray-500 line-through">{String(l.old).slice(0, 30)}</span>{' '}
                            <span className="text-purple-300">{String(l.new).slice(0, 30)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-white/[0.04] border border-white/10 rounded-xl p-4 text-sm text-gray-400" data-testid="pc-no-significant">
                  Aucun changement métier important détecté — seules les quantités/timestamps techniques seront mis à jour.
                </div>
              )}

              <div className="text-[11px] text-gray-500 italic">
                ⚠️ Aperçu local — l'opération n'a pas encore été envoyée au serveur. Cliquez « Appliquer ces changements » pour valider, ou « Retour » pour ajuster.
              </div>
            </div>
          )}

          {/* ───────── STEP 5 — APPLYING ───────── */}
          {step === 'applying' && (
            <div className="text-center py-10">
              <Loader2 className="w-8 h-8 text-emerald-400 animate-spin mx-auto" />
              <p className="text-sm text-gray-400 mt-2">Application en cours…</p>
            </div>
          )}

          {/* ───────── STEP 6 — DONE ───────── */}
          {step === 'done' && applyResult && (
            <div className="space-y-3" data-testid="csv-result">
              <div className="text-base font-semibold text-white flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-400" /> Import terminé
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
                {[
                  { label: 'créé(s)', value: applyResult.created || 0, cls: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300' },
                  { label: 'mis à jour', value: applyResult.updated || 0, cls: 'bg-blue-500/10 border-blue-500/30 text-blue-300' },
                  { label: 'ignoré(s)', value: applyResult.skipped || 0, cls: 'bg-amber-500/10 border-amber-500/30 text-amber-300' },
                  { label: 'fusionné(s)', value: applyResult.merged || 0, cls: 'bg-purple-500/10 border-purple-500/30 text-purple-300' },
                  { label: 'erreur(s)', value: (applyResult.errors || []).length, cls: 'bg-red-500/10 border-red-500/30 text-red-300' },
                ].map((c) => (
                  <div key={c.label} className={`border rounded-lg px-2 py-2 ${c.cls}`} data-testid={`result-${c.label}`}>
                    <div className="text-xl font-bold">{c.value}</div>
                    <div className="text-[10px] opacity-70">{c.label}</div>
                  </div>
                ))}
              </div>
              {(applyResult.errors || []).length > 0 && (
                <details className="text-xs">
                  <summary className="text-red-300 cursor-pointer">Voir les {applyResult.errors.length} erreur(s)</summary>
                  <ul className="mt-1 text-red-200 list-disc pl-5 max-h-32 overflow-y-auto">
                    {applyResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>

        <footer className="p-4 border-t border-white/10 flex items-center justify-between gap-3">
          <div className="text-xs text-gray-500">
            {step === 'upload' && valid.length > 0 && <>{valid.length} ligne(s) prêtes à analyser.</>}
            {step === 'upload' && invalid.length > 0 && valid.length > 0 && <> Les {invalid.length} lignes en erreur seront ignorées.</>}
            {confirmedLarge && valid.length > LARGE_ROW_THRESHOLD && (
              <div className="text-amber-300 mt-1">⚠️ Plus de {LARGE_ROW_THRESHOLD} lignes — recliquez pour confirmer.</div>
            )}
            {step === 'strategy' && globalPolicy === 'per_line' && <>Cliquez « Suivant » pour résoudre chaque conflit individuellement.</>}
            {step === 'perline' && previewData && (
              <>{Object.keys(lineDecisions).length} / {previewData.conflicts.length} décision(s) prise(s) (défaut = MAJ).</>
            )}
          </div>
          <div className="flex gap-2">
            {step !== 'upload' && step !== 'done' && step !== 'applying' && (
              <button onClick={() => {
                if (step === 'preview-changes') setStep(globalPolicy === 'per_line' ? 'perline' : 'strategy');
                else if (step === 'perline') setStep('strategy');
                else setStep('upload');
              }}
                className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 text-sm flex items-center gap-1.5"
                data-testid="csv-back"
              >
                <ArrowLeft className="w-4 h-4" /> Retour
              </button>
            )}
            <button onClick={handleClose} className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 text-sm" data-testid="csv-cancel">
              {step === 'done' ? 'Fermer' : 'Annuler'}
            </button>
            {step === 'upload' && (
              <button
                onClick={runPreview}
                disabled={busy || valid.length === 0}
                className="px-4 py-2 rounded-lg bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/30 disabled:opacity-40 text-sm font-medium flex items-center gap-2"
                data-testid="csv-continue"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                Continuer ({valid.length})
              </button>
            )}
            {step === 'strategy' && globalPolicy !== 'per_line' && (
              <button onClick={goToPreviewChanges} disabled={busy}
                className="px-4 py-2 rounded-lg bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/30 disabled:opacity-40 text-sm font-medium flex items-center gap-2"
                data-testid="csv-apply"
              >
                <ArrowRight className="w-4 h-4" />
                Aperçu ({(stats.new || 0)} créer + {(stats.conflicts || 0)} {globalPolicy === 'skip' ? 'ignorer' : globalPolicy === 'duplicate' ? 'dupliquer' : 'mettre à jour'})
              </button>
            )}
            {step === 'strategy' && globalPolicy === 'per_line' && (
              <button onClick={() => setStep('perline')}
                className="px-4 py-2 rounded-lg bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/30 text-sm font-medium flex items-center gap-2"
                data-testid="csv-goto-perline"
              >
                <ListChecks className="w-4 h-4" /> Suivant
              </button>
            )}
            {step === 'perline' && (
              <button onClick={goToPreviewChanges} disabled={busy}
                className="px-4 py-2 rounded-lg bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/30 text-sm font-medium flex items-center gap-2"
                data-testid="csv-apply-perline"
              >
                <ArrowRight className="w-4 h-4" />
                Aperçu des changements
              </button>
            )}
            {step === 'preview-changes' && (
              <button onClick={runApply} disabled={busy}
                className="px-4 py-2 rounded-lg bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/30 disabled:opacity-40 text-sm font-medium flex items-center gap-2"
                data-testid="csv-apply-confirm"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Appliquer ces changements
              </button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
};

export default CSVImportModal;
